import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { MessageCircle, Loader2, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function WhatsAppSettings() {
  const qc = useQueryClient();
  const [busy, setBusy] = React.useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["whatsapp"], queryFn: () => api.get("/whatsapp").then((r) => r.data) });
  const { data: logs = [] } = useQuery({ queryKey: ["whatsapp-logs"], queryFn: () => api.get("/whatsapp/logs").then((r) => r.data) });
  const { data: creditHistory = [] } = useQuery({ queryKey: ["wa-credit-history"], queryFn: () => api.get("/whatsapp/credit-history").then((r) => r.data) });

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const reasonLabel = (r) => (r === "admin_add" ? "Kontör yüklendi" : "Mesaj gönderimi");

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      await api.post("/whatsapp/toggle", { enabled });
      qc.invalidateQueries({ queryKey: ["whatsapp"] });
      toast.success(enabled ? "WhatsApp bildirimleri açıldı" : "WhatsApp bildirimleri kapatıldı");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (isLoading || !data)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="whatsapp-page">
      <div>
        <h1 className="text-3xl font-bold">WhatsApp Bildirimleri</h1>
        <p className="text-muted-foreground mt-1">Müşterilerinize otomatik onay ve hatırlatma mesajları</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 flex items-center justify-between gap-4" data-testid="whatsapp-toggle-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold">WhatsApp mesajları</p>
            <p className="text-sm text-muted-foreground">Rezervasyon onayı ve randevu hatırlatması otomatik gönderilsin</p>
          </div>
        </div>
        <Switch checked={data.enabled} onCheckedChange={toggle} disabled={busy} data-testid="whatsapp-toggle" />
      </div>

      <div className={cn("rounded-xl border p-4 flex items-start gap-3 text-sm",
        data.desk360_live ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800")}
        data-testid="whatsapp-platform-status">
        {data.desk360_live ? <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" /> : <Info className="w-5 h-5 shrink-0 mt-0.5" />}
        <p>
          {data.desk360_live
            ? <>WhatsApp altyapısı aktif — mesajlar gerçek olarak gönderiliyor. Kalan bildirim hakkınız: <b data-testid="wa-credits-count">{data.wa_credits}</b> kontör.
                {data.wa_credits === 0 && " Kontörünüz bitti; Abonelik sayfasından ek paket satın alabilirsiniz."}</>
            : "WhatsApp altyapısı henüz platform tarafından etkinleştirilmedi."}
        </p>
      </div>

      <div data-testid="wa-credit-history-section">
        <h2 className="text-lg font-semibold mb-3">Kontör Kullanım Geçmişi</h2>
        {creditHistory.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm" data-testid="no-credit-history">
            Henüz kontör hareketi yok.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
            {creditHistory.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid="credit-history-item">
                <div>
                  <p className="text-sm font-medium">{reasonLabel(h.reason)}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(h.created_at)}</p>
                </div>
                <div className="text-right">
                  <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full",
                    h.amount > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600")}>
                    {h.amount > 0 ? `+${h.amount}` : h.amount}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">Kalan: <b className="text-foreground">{h.remaining}</b></p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Gönderilen Mesajlar</h2>
        {logs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm" data-testid="no-wa-logs">
            Henüz mesaj gönderilmedi.
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((l) => (
              <div key={l.id} className="rounded-xl border border-border bg-card p-4" data-testid="wa-log-item">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{l.to}</span>
                  <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full",
                    l.mode === "mock" ? "bg-amber-100 text-amber-700" :
                    l.mode === "disabled" ? "bg-slate-100 text-slate-600" :
                    l.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700")}>
                    {l.mode === "mock" ? "Test" : l.mode === "disabled" ? "Kapalı" : l.status === "no_credits" ? "Kontör Yok" : "Canlı"} · {l.status}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground whitespace-pre-line">{l.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
